import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getSavedMappingAPI,
  resolveFieldsService,
} from '../../apis/EntitiesSelection';
import { fetchSqlTableFields, getConnectionById } from '../../apis/Connections';
import { buildInitialNodes } from '../../helpers/buildInitialNodes';
import { runFuzzyLogicAPI, runFieldMappingAPI } from '../../apis/FuzzylogicAPI';
import { postMappingDetails } from '../../apis/MappingAPI';
import _cloneDeep from 'lodash/cloneDeep';
import { pruneInvalidEdgesAndFunctionChains } from '../../utils/arrayUtils/pruneInvalidEdgesFunc';

const getConnectorName = (connection = {}) => {
  const raw =
    connection?.connector_name ||
    connection?.connector ||
    connection?.type ||
    '';
  return typeof raw === 'string' ? raw.toLowerCase() : '';
};

const isSqlMappingConfig = (config = {}) => {
  const connector =
    config?.connection?.connector_name ||
    config?.connection?.connector ||
    config?.label ||
    config?.type ||
    '';
  return typeof connector === 'string'
    ? connector.toLowerCase().includes('sql')
    : false;
};

const buildSqlMetadataFromConnection = (connection = {}) => {
  if (!connection) return null;
  if (getConnectorName(connection) !== 'sql') return null;
  const params = connection.custom_params || {};
  const token =
    params.token || params.quick_test_token || connection.token || null;
  return {
    token: token || null,
    host: params.host || '',
    port:
      params.port !== undefined && params.port !== null
        ? String(params.port)
        : '',
    database: params.database_name || params.db_name || params.database || '',
  };
};

const ensureSqlMetadataForConfig = async (config = {}) => {
  if (!config) return null;
  const direct = config.sqlMetadata || config.connection?.sqlMetadata || null;
  if (direct?.token) return direct;
  const inline = buildSqlMetadataFromConnection(config.connection || {});
  if (inline?.token) return inline;
  if (config.connection?.conn_id) {
    try {
      const connection = await getConnectionById(config.connection.conn_id);
      const hydrated = buildSqlMetadataFromConnection(connection);
      if (hydrated?.token) return hydrated;
      return hydrated || direct || null;
    } catch (err) {
      console.error('Failed to hydrate SQL metadata', err);
    }
  }
  return direct || inline || null;
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.values(value);
  return [];
};

const normalizeSqlFieldsResponse = (response) => {
  if (!response) return [];
  const candidates =
    response.fields?.fields ||
    response.fields ||
    response.columns ||
    response.schema ||
    response.data ||
    response.result ||
    response;
  return asArray(candidates)
    .map((field) => {
      if (!field || typeof field !== 'object') return null;
      const fieldName =
        field.field_name ||
        field.column_name ||
        field.name ||
        field.columnName ||
        '';
      if (!fieldName) return null;
      const label =
        field.field_label ||
        field.display_name ||
        field.column_label ||
        field.column_name ||
        field.name ||
        fieldName;
      const nullableFlag =
        field.nullable === false || field.is_nullable === false
          ? true
          : undefined;
      const mandatory =
        field.is_mandatory ?? field.is_required ?? nullableFlag ?? false;
      return {
        field_name: fieldName,
        data_type: (
          field.data_type ||
          field.type ||
          field.column_type ||
          field.columnType ||
          'string'
        ).toLowerCase(),
        field_label: label,
        is_mandatory: Boolean(mandatory),
        is_creatable:
          field.is_creatable ?? field.isWritable ?? field.is_writable ?? true,
        is_updatable:
          field.is_updatable ?? field.isWritable ?? field.is_writable ?? true,
        is_queryable:
          field.is_queryable ?? field.isQueryable ?? field.is_queryable ?? true,
        is_primary_key:
          field.is_primary_key === true ||
          field.isPrimaryKey === true ||
          field.is_primary === true ||
          field.isPrimary === true ||
          false,
      };
    })
    .filter(Boolean);
};

const buildSqlResolvedEntries = async (configs = []) => {
  if (!Array.isArray(configs) || configs.length === 0) return [];
  const tasks = configs.map(async (config) => {
    // Ensure config.id is always set - use nodeId if id is missing
    // This prevents node IDs from changing between calls
    const nodeId = config.id || config.nodeId;
    if (!nodeId) {
      console.warn(
        'SQL config missing id/nodeId:',
        config,
        'This may cause node ID inconsistencies',
      );
    }

    const metadata = await ensureSqlMetadataForConfig(config);
    if (!metadata?.token) {
      throw new Error(
        `SQL connector "${
          config?.connection?.conn_name || config?.label || nodeId
        }" is missing credentials. Please re-save the connection.`,
      );
    }

    const entities = Array.isArray(config.selectedEntities)
      ? config.selectedEntities
      : [];
    const perEntity = await Promise.all(
      entities.map(async (entity) => {
        const tableName =
          entity?.entity_name || entity?.entity || entity?.label || entity;
        if (!tableName) return null;
        const response = await fetchSqlTableFields(metadata.token, tableName);
        const fields = normalizeSqlFieldsResponse(response);
        return {
          node_id: nodeId, // Use the ensured nodeId
          type: config.type || 'source',
          entity: tableName,
          entity_name: tableName,
          connection: config.connection,
          fields: {
            status: 'success',
            source: config.connection?.connector_name || 'SQL',
            module_name: 'SQL',
            entity_name: tableName,
            fields,
          },
        };
      }),
    );
    return perEntity.filter(Boolean);
  });
  const resolved = await Promise.all(tasks);
  return resolved.flat();
};

export const fetchResolvedFields = createAsyncThunk(
  'mapping/fetchResolvedFields',
  async (
    { label, workflowId, finalConfigPayload, ...rest },
    { rejectWithValue, getState },
  ) => {
    try {
      const configs = Array.isArray(finalConfigPayload)
        ? finalConfigPayload
        : [];
      const sqlConfigs = [];
      const nonSqlConfigs = [];

      // For SQL configs, ensure id is set from existing nodes if missing
      // This prevents node IDs from changing between calls
      const state = getState();
      const existingNodes =
        state.mapping?.mappingsByWorkflow?.[workflowId]?.nodes || [];

      configs.forEach((cfg) => {
        if (isSqlMappingConfig(cfg)) {
          // If config.id is missing, try to find it from existing nodes
          if (!cfg.id && cfg.nodeId) {
            cfg.id = cfg.nodeId;
          } else if (!cfg.id && !cfg.nodeId) {
            // Try to find matching node by connection ID and type
            const matchingNode = existingNodes.find((node) => {
              const nodeConnId = node?.data?.config?.connection?.conn_id;
              const cfgConnId = cfg?.connection?.conn_id;
              const nodeType = node.type;
              const cfgType = cfg.type;
              return (
                nodeConnId &&
                cfgConnId &&
                nodeConnId === cfgConnId &&
                ((nodeType === 'sourceNode' && cfgType === 'source') ||
                  (nodeType === 'destinationNode' && cfgType === 'destination'))
              );
            });
            if (matchingNode) {
              cfg.id = matchingNode.id;
              console.log(
                'Restored SQL config.id from existing node:',
                matchingNode.id,
              );
            }
          }
          sqlConfigs.push(cfg);
        } else {
          nonSqlConfigs.push(cfg);
        }
      });

      let result = [];

      if (nonSqlConfigs.length > 0) {
        const resolved = await resolveFieldsService({
          label,
          configs: nonSqlConfigs,
          ...rest,
        });
        result = Array.isArray(resolved) ? resolved : [];
      }

      if (sqlConfigs.length > 0) {
        const sqlResolved = await buildSqlResolvedEntries(sqlConfigs);
        result = [...result, ...sqlResolved];
      }

      return { workflowId, result };
    } catch (err) {
      // Handle timeout errors specifically
      if (
        err.isTimeout ||
        err.code === 'ECONNABORTED' ||
        err.message?.includes('timeout')
      ) {
        return rejectWithValue({
          message:
            'Request timeout. The server is taking too long to respond. Please try again.',
          isTimeout: true,
        });
      }
      return rejectWithValue(
        err.response?.data ||
          err.message ||
          'An error occurred while fetching resolved fields',
      );
    }
  },
);

export const runFuzzyLogicIfNeeded = createAsyncThunk(
  'mapping/runFuzzyLogicIfNeeded',
  async (payload, { getState, rejectWithValue }) => {
    const workflowId = payload.workflowId;
    const state = getState();

    if (state.mapping?.fuzzyLogicRunByWorkflow?.[workflowId]) {
      return { alreadyRun: true };
    }

    try {
      const result = await runFuzzyLogicAPI(payload);
      return { result, workflowId };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const runFieldMappingIfNeeded = createAsyncThunk(
  'mapping/runFieldMappingIfNeeded',
  async (payload, { getState, rejectWithValue, dispatch }) => {
    const { workflowId, force = false } = payload;
    const state = getState();

    if (!force && state.mapping?.fieldMappingRunByWorkflow?.[workflowId]) {
      return { alreadyRun: true, hasFuzzyEdges: true }; // default to true
    }

    try {
      const result = await runFieldMappingAPI(payload);

      const workflowData = state.mapping.mappingsByWorkflow?.[workflowId];
      // const nodes = workflowData?.nodes || [];
      const originalNodes = workflowData?.nodes || [];
      const clonedNodes = _cloneDeep(originalNodes);
      const edges = workflowData?.edges || [];
      const mappedFields = result?.mapped_fields || {};
      const hasFuzzyEdges = Object.keys(mappedFields).length > 0;
      console.log('mappedFields:', mappedFields);

      const sourceNodes = clonedNodes.filter((n) => n.type === 'sourceNode');
      const targetNodes = clonedNodes.filter(
        (n) => n.type === 'destinationNode',
      );
      const newEdges = [];

      const matchesEntityId = (candidateId, entityName) => {
        if (!candidateId || !entityName) return false;
        if (candidateId === entityName) return true;
        const parts = candidateId.split('__');
        return parts.length > 1 && parts[parts.length - 1] === entityName;
      };

      const matchesFieldId = (candidateId, entityName, fieldName) => {
        if (!candidateId || !entityName || !fieldName) return false;
        const expected = `${entityName}__${fieldName}`;
        if (candidateId === expected) return true;
        const parts = candidateId.split('__');
        if (parts.length < 2) return false;
        const suffix = parts.slice(-2).join('__');
        return suffix === expected;
      };

      const findFieldInNodes = (nodes, entityName, fieldName) => {
        for (const node of nodes) {
          const groups = node?.data?.subFields || [];
          for (const group of groups) {
            if (!matchesEntityId(group.id, entityName)) continue;
            const field = (group.children || []).find((child) =>
              matchesFieldId(child.id, entityName, fieldName),
            );
            if (field) {
              return { node, handle: field.id, field };
            }
          }
        }
        return { node: null, handle: null, field: null };
      };

      const mappedHandles = new Set();

      edges.forEach((edge) => {
        console.log('edge:', edge);
        if (edge.sourceHandle) mappedHandles.add(edge.sourceHandle);
        if (edge.targetHandle) mappedHandles.add(edge.targetHandle);
      });

      const mappedEntities = state.mapping?.mappedEntitiesByWorkflow?.[
        workflowId
      ] || {
        Source: [],
        Destination: [],
      };

      for (const [sourceKey, targetKey] of Object.entries(mappedFields)) {
        const [sourceEntity, sourceField] = sourceKey.split('.');
        const [targetEntity, targetField] = targetKey.split('.');

        const isAlreadyMappedEntity =
          mappedEntities.Source.includes(sourceEntity) &&
          mappedEntities.Destination.includes(targetEntity);

        if (isAlreadyMappedEntity) continue;

        const {
          node: matchedSourceNode,
          handle: sourceHandle,
          field: sourceFieldObj,
        } = findFieldInNodes(sourceNodes, sourceEntity, sourceField);

        const {
          node: matchedTargetNode,
          handle: targetHandle,
          field: targetFieldObj,
        } = findFieldInNodes(targetNodes, targetEntity, targetField);

        if (
          !matchedSourceNode ||
          !matchedTargetNode ||
          !sourceHandle ||
          !targetHandle
        )
          continue;

        const isSourceMapped = mappedHandles.has(sourceHandle);
        const isTargetMapped = mappedHandles.has(targetHandle);

        if (isSourceMapped) continue;
        if (isTargetMapped) continue;

        if (targetFieldObj?.is_creatable === false) continue;
        // if(targetFieldObj?.label === "Billing Country") continue;

        const sourceType = sourceFieldObj?.type || 'unknown';
        const targetType = targetFieldObj?.type || 'unknown';

        const sourceLabel = sourceFieldObj?.label || 'unknown';
        const targetLabel = targetFieldObj?.label || 'unknown';

        console.log('targetFieldObj', targetFieldObj);

        const isValidType = sourceType === targetType;

        if (isValidType) {
          if (sourceFieldObj) {
            sourceFieldObj.isMapped = true;
            sourceFieldObj.isMismatch = false;
          }
          if (targetFieldObj) {
            targetFieldObj.isMapped = true;
            targetFieldObj.isMismatch = false;
          }

          newEdges.push({
            id: `${matchedSourceNode.id}-${sourceHandle}__${matchedTargetNode.id}-${targetHandle}`,
            source: matchedSourceNode.id,
            target: matchedTargetNode.id,
            sourceHandle,
            targetHandle,
            type: 'custom',
            animated: true,
            data: {
              label: isValidType ? 'Auto Mapped' : 'Type Mismatch',
              isValid: isValidType,
              sourceType,
              targetType,
              source: 'fuzzy',
              isFuzzy: true,
              isManual: false,
              sourceLabel,
              targetLabel,
            },
            style: isValidType
              ? { strokeWidth: 1.5 }
              : { strokeWidth: 2, stroke: 'red' },
            markerEnd: {
              type: 'arrowclosed',
              color: isValidType ? undefined : 'red',
            },
          });

          mappedHandles.add(targetHandle);
        }
      }

      if (newEdges.length) {
        dispatch(
          setEdgesForWorkflow({ workflowId, edges: [...edges, ...newEdges] }),
        );

        dispatch(setNodesForWorkflow({ workflowId, nodes: clonedNodes }));
      }

      return { result, workflowId, hasFuzzyEdges };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const applySavedMappingIfNeeded = createAsyncThunk(
  'mapping/applySavedMappingIfNeeded',
  async (
    { workflowId, mappings, isOneToMany },
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const state = getState();
      const nodes = _cloneDeep(
        state.mapping.mappingsByWorkflow?.[workflowId]?.nodes || [],
      );
      // const sourceNode = nodes.find((n) => n.type === 'sourceNode');
      // const destinationNode = nodes.find((n) => n.type === 'destinationNode');

      // if (!sourceNode || !destinationNode) {
      //   throw new Error('Source or Destination node not found');
      // }

      const edges = [];

      const getEdgeStyleProps = ({
        isManual,
        isFuzzy,
        isFunctionEdge,
        isValid,
      }) => {
        let animated = false;
        let color = 'gray';

        if (isFuzzy) {
          color = isValid ? 'blue' : 'red';
          animated = true;
        } else if (isFunctionEdge) {
          color = isValid ? 'blue' : 'red';
          animated = false;
        } else if (isManual) {
          color = isValid ? 'green' : 'red';
          animated = false;
        }

        return { animated, color };
      };

      const getFieldType = (node, fieldId) => {
        for (const group of node.data.subFields || []) {
          for (const field of group.children || []) {
            if (field.id === fieldId) return field.type || 'unknown';
          }
        }
        return 'unknown';
      };

      const markMapped = (node, fieldId, { isMismatch, isFuzzy, isManual }) => {
        node.data.subFields.forEach((group) => {
          group.children.forEach((field) => {
            if (field.id === fieldId) {
              console.log(
                `Mapping field ${fieldId}: isFuzzy=${isFuzzy}, isMismatch=${isMismatch}`,
              );
              field.isMapped = true;
              field.isMismatch = isMismatch;

              if (isFuzzy === true && isManual === false) {
                field.color = isMismatch ? 'red' : 'green';
              } else {
                delete field.color;
              }
            }
          });
        });
      };

      // Helper to extract entity and field from field ID
      const extractEntityField = (fieldId) => {
        if (!fieldId || typeof fieldId !== 'string') return null;
        const parts = fieldId.split('__');
        if (parts.length >= 3) {
          // Format: nodeId__entity__field
          return {
            entity: parts[parts.length - 2]?.toLowerCase() || '',
            field: parts[parts.length - 1]?.toLowerCase() || '',
          };
        } else if (parts.length === 2) {
          // Format: entity__field
          return {
            entity: parts[0]?.toLowerCase() || '',
            field: parts[1]?.toLowerCase() || '',
          };
        }
        return null;
      };

      const resolveHandleInNode = (node, rawId, parentId) => {
        if (!node) return null;
        const groups = node.data?.subFields || [];

        // Try exact match first
        for (const g of groups)
          for (const f of g.children || []) if (f.id === rawId) return f.id;

        // Try with parentId if rawId doesn't include '__'
        if (parentId && !rawId.includes('__')) {
          const tryExact = `${parentId}__${rawId}`;
          for (const g of groups)
            for (const f of g.children || [])
              if (f.id === tryExact) return f.id;
        }

        // Try suffix match (last part after '__')
        const suffix = rawId.includes('__')
          ? rawId.split('__').slice(-1)[0]
          : rawId;
        for (const g of groups)
          for (const f of g.children || [])
            if (f.id?.endsWith(`__${suffix}`)) return f.id;

        // For SQL connectors: try entity+field matching (case-insensitive)
        // This handles cases where node IDs change but entity/field names remain the same
        const entityField = extractEntityField(rawId);
        if (entityField && entityField.entity && entityField.field) {
          for (const g of groups) {
            // Check if group matches entity (case-insensitive)
            const groupEntity = g.id.includes('__')
              ? g.id.split('__').slice(-1)[0]?.toLowerCase()
              : g.id?.toLowerCase();

            if (groupEntity === entityField.entity) {
              // Find field by name (case-insensitive)
              for (const f of g.children || []) {
                const fieldName = f.id.includes('__')
                  ? f.id.split('__').slice(-1)[0]?.toLowerCase()
                  : f.id?.toLowerCase();

                if (fieldName === entityField.field) {
                  return f.id; // Return the actual field ID from the node
                }
              }
            }
          }
        }

        return null;
      };

      // const resolveFullSourceHandle = (node, src) => {
      //   const rawId = typeof src === 'string' ? src : src?.fieldId;
      //   const parentId = typeof src === 'string' ? '' : (src?.parentId || '');
      //   if (!rawId) return null;

      //   if (rawId.includes('__')) {
      //     const exists = (node.data?.subFields || []).some(group =>
      //       (group.children || []).some(f => f.id === rawId)
      //     );
      //     if (exists) return rawId;
      //   }

      //   const tryExact = parentId ? `${parentId}__${rawId}` : null;
      //   if (tryExact) {
      //     const exactExists = (node.data?.subFields || []).some(group =>
      //       (group.children || []).some(f => f.id === tryExact)
      //     );
      //     if (exactExists) return tryExact;
      //   }

      //   for (const group of node.data?.subFields || []) {
      //     for (const child of group.children || []) {
      //       if (child?.id?.endsWith(`__${rawId}`)) {
      //         return child.id;
      //       }
      //     }
      //   }

      //   return null;
      // };

      // Helper to extract entity from field ID or parent ID
      const extractEntityFromId = (id) => {
        if (!id || typeof id !== 'string') return null;
        const parts = id.split('__');
        if (parts.length >= 2) {
          return parts[parts.length - 1]?.toLowerCase() || null;
        }
        return null;
      };

      // Helper to find node by type and entity (for when node IDs change)
      const findNodeByTypeAndEntity = (nodes, nodeType, entityName) => {
        if (!entityName) return null;
        return nodes.find((n) => {
          if (n.type !== nodeType) return false;
          const groups = n.data?.subFields || [];
          return groups.some((g) => {
            const groupEntity = g.id.includes('__')
              ? g.id.split('__').slice(-1)[0]?.toLowerCase()
              : g.id?.toLowerCase();
            return groupEntity === entityName.toLowerCase();
          });
        });
      };

      mappings.forEach((map, i) => {
        const getNodeIdFromFieldId = (fieldId) =>
          (fieldId || '').split('__')[0];

        const isAggregation =
          map?.mappingMeta?.isAggregation ||
          (Array.isArray(map.functionChain) &&
            map.functionChain.some((s) => s.functionName === 'aggregation'));

        // Try to find destination node - prioritize map.destinationNodeId for decision flows
        let destinationNode = null;
        let destinationNodeId = null;

        // First, try using map.destinationNodeId if it exists (for decision flows)
        if (map.destinationNodeId) {
          destinationNodeId = map.destinationNodeId;
          destinationNode = nodes.find((n) => n.id === destinationNodeId);
        }

        // If not found, try extracting from destinationFieldId (backward compatibility)
        if (!destinationNode) {
          destinationNodeId = getNodeIdFromFieldId(map.destinationFieldId);
          destinationNode = nodes.find((n) => n.id === destinationNodeId);
        }

        // If still not found, try to find by entity name (fallback for SQL connectors)
        if (!destinationNode) {
          const destEntity = extractEntityFromId(map.destinationParentId);
          if (destEntity) {
            destinationNode = findNodeByTypeAndEntity(
              nodes,
              'destinationNode',
              destEntity,
            );
            // Update destinationNodeId for logging if found by entity
            if (destinationNode) {
              destinationNodeId = destinationNode.id;
            }
          }
        }

        if (!destinationNode) {
          console.warn('Could not find destination node for mapping:', {
            destinationNodeId,
            destinationFieldId: map.destinationFieldId,
            destinationParentId: map.destinationParentId,
          });
          return;
        }

        if (isAggregation) {
          const aggStep =
            (map.functionChain || []).find(
              (s) => s.functionName === 'aggregation',
            ) || {};
          const aggNodeId = `functionNode-agg-${i}`;

          nodes.push({
            id: aggNodeId,
            type: 'functionNode',
            position: aggStep.position || { x: 600, y: i * 120 + 120 },
            draggable: true,
            data: {
              label: 'aggregation',
              functionType: 'aggregation',
              config: aggStep.config || {},
              destinationFieldId: map.destinationFieldId,
              aggregationSourceFields: map.aggregationSourceFields || [],
            },
          });

          const styleIn = getEdgeStyleProps({
            isManual: true,
            isFuzzy: false,
            isFunctionEdge: true,
            isValid: true,
          });

          (map.aggregationSourceFields || []).forEach((src) => {
            const fullHandle = `${src.parentId}__${src.fieldId}`;
            const srcNodeId = getNodeIdFromFieldId(src.parentId);
            const srcNode = nodes.find((n) => n.id === srcNodeId);
            if (!srcNode) return;

            // markMapped(srcNode, fullHandle, { isMismatch: false, isFuzzy: false, isManual: true });

            const fullCandidate = `${src.parentId}__${src.fieldId}`;
            const srcHandle = resolveHandleInNode(
              srcNode,
              fullCandidate,
              src.parentId,
            );
            if (!srcHandle) return;
            markMapped(srcNode, srcHandle, {
              isMismatch: false,
              isFuzzy: false,
              isManual: true,
            });

            edges.push({
              id: `edge-${srcNodeId}-${fullHandle}__${aggNodeId}`,
              source: srcNodeId,
              target: aggNodeId,
              sourceHandle: fullHandle,
              type: 'custom',
              animated: styleIn.animated,
              data: {
                label: 'Valid',
                functionType: 'aggregation',
                isValid: true,
                isFunctionEdge: true,
                isManual: true,
                isFuzzy: false,
              },
              style: { stroke: styleIn.color, strokeWidth: 1.5 },
              markerEnd: { type: 'arrowclosed', color: styleIn.color },
            });
          });

          const destHandle = resolveHandleInNode(
            destinationNode,
            map.destinationFieldId,
            map.destinationParentId,
          );
          if (!destHandle) return;

          const styleOut = getEdgeStyleProps({
            isManual: true,
            isFuzzy: false,
            isFunctionEdge: true,
            isValid: true,
          });
          edges.push({
            // id: `edge-${aggNodeId}-to-${destinationNode.id}-${map.destHandle}`,
            id: `edge-${aggNodeId}-to-${destinationNode.id}-${destHandle}`,
            source: aggNodeId,
            target: destinationNode.id,
            // targetHandle: map.destinationFieldId,
            targetHandle: destHandle,
            type: 'custom',
            animated: styleOut.animated,
            data: {
              label: 'Valid',
              isValid: true,
              isFunctionEdge: true,
              isManual: true,
              isFuzzy: false,
            },
            style: { stroke: styleOut.color, strokeWidth: 1.5 },
            markerEnd: { type: 'arrowclosed', color: styleOut.color },
          });

          // markMapped(destinationNode, map.destinationFieldId, {
          markMapped(destinationNode, destHandle, {
            isMismatch: false,
            isFuzzy: false,
            isManual: true,
          });
          return;
        }

        // Try to find source node by ID first
        const sourceNodeId = getNodeIdFromFieldId(map.sourceFieldId);
        let sourceNode = nodes.find((n) => n.id === sourceNodeId);

        // If not found and we have SQL connector, try to find by entity name
        if (!sourceNode) {
          const sourceEntity = extractEntityFromId(map.sourceParentId);
          if (sourceEntity) {
            sourceNode = findNodeByTypeAndEntity(
              nodes,
              'sourceNode',
              sourceEntity,
            );
          }
        }

        if (!sourceNode) {
          console.warn('Could not find source node for mapping:', {
            sourceNodeId,
            sourceFieldId: map.sourceFieldId,
            sourceParentId: map.sourceParentId,
          });
          return;
        }

        // const sourceNodeId = getNodeIdFromFieldId(map.sourceFieldId);
        // const destinationNodeId = getNodeIdFromFieldId(map.destinationFieldId);

        // const sourceNode = nodes.find(n => n.id === sourceNodeId);
        // const destinationNode = nodes.find(n => n.id === destinationNodeId);

        console.log(
          'DEBUG Mapping',
          map.sourceFieldId,
          'sourceNodeId:',
          sourceNodeId,
          'sourceNode:',
          sourceNode?.id,
          '|',
          map.destinationFieldId,
          'destinationNodeId:',
          destinationNodeId,
          'destinationNode:',
          destinationNode?.id,
        );

        // Resolve actual field IDs (handles node ID changes)
        const resolvedSourceHandle = resolveHandleInNode(
          sourceNode,
          map.sourceFieldId,
          map.sourceParentId,
        );
        const resolvedDestHandle = resolveHandleInNode(
          destinationNode,
          map.destinationFieldId,
          map.destinationParentId,
        );

        if (!resolvedSourceHandle || !resolvedDestHandle) {
          console.warn('Could not resolve handles for mapping:', {
            sourceFieldId: map.sourceFieldId,
            resolvedSourceHandle,
            destinationFieldId: map.destinationFieldId,
            resolvedDestHandle,
          });
          return;
        }

        const sourceType = getFieldType(sourceNode, resolvedSourceHandle);
        const targetType = getFieldType(destinationNode, resolvedDestHandle);
        const isFuzzy = map.mappingMeta?.isFuzzy ?? false;
        const isManual = map.mappingMeta?.isManual ?? false;
        const isValid =
          map.mappingMeta?.isValid ??
          (isManual || isFuzzy ? sourceType === targetType : true);

        const label = isValid ? 'Valid' : 'Type Mismatch';

        markMapped(sourceNode, resolvedSourceHandle, {
          isMismatch: !isValid,
          isFuzzy,
          isManual,
        });

        markMapped(destinationNode, resolvedDestHandle, {
          isMismatch: !isValid,
          isFuzzy,
          isManual,
        });

        console.log('Field marking', {
          fieldId: map.sourceFieldId,
          isFuzzy,
          isManual,
        });

        if (Array.isArray(map.functionChain) && map.functionChain.length > 0) {
          let prevNodeId = sourceNode.id;
          let prevHandle = resolvedSourceHandle;
          const fnNodeIds = [];

          map.functionChain.forEach((funcStep, j) => {
            const functionType = funcStep.functionName || '';
            const fnNodeId = `functionNode-${i}-${j}`;

            nodes.push({
              id: fnNodeId,
              type: 'functionNode',
              position: funcStep.position || {
                x: 600 + 120 * j,
                y: i * 100 + 100,
              },
              draggable: true,
              data: {
                label: functionType,
                functionType,
                config: funcStep.config || {},
                sourceFieldId: resolvedSourceHandle,
                destinationFieldId: resolvedDestHandle,
              },
            });

            const { animated, color } = getEdgeStyleProps({
              isManual,
              isFuzzy,
              isFunctionEdge: true,
              isValid,
            });

            edges.push({
              id: `edge-${prevNodeId}-${prevHandle || 'auto'}-${fnNodeId}`,
              source: prevNodeId,
              target: fnNodeId,
              sourceHandle: prevHandle,
              type: 'custom',
              animated,
              data: {
                label,
                sourceType,
                functionType,
                isValid,
                isFunctionEdge: true,
                isManual,
                isFuzzy,
              },
              style: {
                stroke: color,
                strokeWidth: 1.5,
              },
              markerEnd: { type: 'arrowclosed', color },
            });

            prevNodeId = fnNodeId;
            prevHandle = undefined;
            fnNodeIds.push(fnNodeId);
          });

          const { animated: lastAnimated, color: lastColor } =
            getEdgeStyleProps({
              isManual,
              isFuzzy,
              isFunctionEdge: true,
              isValid,
            });

          edges.push({
            id: `edge-${fnNodeIds.at(-1)}-to-${destinationNode.id}-${resolvedDestHandle}`,
            source: fnNodeIds.at(-1),
            target: destinationNode.id,
            targetHandle: resolvedDestHandle,
            type: 'custom',
            animated: lastAnimated,
            data: {
              label,
              sourceType,
              targetType,
              isValid,
              isFunctionEdge: true,
              isManual,
              isFuzzy,
            },
            style: {
              stroke: lastColor,
              strokeWidth: 1.5,
            },
            markerEnd: {
              type: 'arrowclosed',
              color: lastColor,
            },
          });
        } else {
          const { animated, color } = getEdgeStyleProps({
            isManual,
            isFuzzy,
            isFunctionEdge: false,
            isValid,
          });

          edges.push({
            id: `edge-${sourceNode.id}-${resolvedSourceHandle}__${destinationNode.id}-${resolvedDestHandle}`,
            source: sourceNode.id,
            target: destinationNode.id,
            sourceHandle: resolvedSourceHandle,
            targetHandle: resolvedDestHandle,
            type: 'custom',
            animated,
            data: {
              label,
              sourceType,
              targetType,
              isValid,
              isManual,
              isFuzzy,
              isFunctionEdge: false,
            },
            style: {
              stroke: color,
              strokeWidth: 1.5,
            },
            markerEnd: {
              type: 'arrowclosed',
              color,
            },
          });
        }
      });
      // dispatch(setNodesForWorkflow({ workflowName, nodes }));
      // dispatch(setEdgesForWorkflow({ workflowName, edges }));

      const pruned = pruneInvalidEdgesAndFunctionChains({
        nodes,
        edges,
      });

      dispatch(setNodesForWorkflow({ workflowId, nodes: pruned.nodes }));
      dispatch(setEdgesForWorkflow({ workflowId, edges: pruned.edges }));

      if (isOneToMany) {
        dispatch(
          setConnectionDetailsForWorkflow({
            workflowId,
            connectionDetails: mappings,
          }),
        );
      }

      return { workflowId };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message || String(err));
    }
  },
);

export const getSavedMapping = createAsyncThunk(
  'mapping/getSavedMapping',
  async (workflowId, { rejectWithValue }) => {
    try {
      const response = await getSavedMappingAPI(workflowId);
      console.log('response of get save:', response);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const saveMappingThunk = createAsyncThunk(
  'mapping/save',
  async ({ workflow_id, mapping_json }, { rejectWithValue }) => {
    try {
      return await postMappingDetails({ workflow_id, mapping_json });
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const initialState = {
  mappingsByWorkflow: {},
  fuzzyLogicRunByWorkflow: {},
  fieldMappingRunByWorkflow: {},
  mappedFields: [],
  saving: false,
  saveSuccess: false,
  saveError: null,
  loading: false,
  error: null,
  autoFuzzyEnabledByWorkflow: {},
  mappedEntitiesByWorkflow: {},
  unmappedEntitiesByWorkflow: {},
  fuzzyAppliedEntitiesByWorkflow: {},
  hasVisitedMappingByWorkflow: {},
  expandAllTriggerByWorkflow: {},
  nodeHeightsByWorkflow: {},
  finalConfigPayloadByWorkflow: {},
  blinkByWorkflow: {},
  filterExpressionByWorkflow: {},
};

const mappingSlice = createSlice({
  name: 'mapping',
  initialState,
  reducers: {
    setNodesForWorkflow(state, action) {
      const { workflowId, nodes } = action.payload;
      if (!state.mappingsByWorkflow) state.mappingsByWorkflow = {};
      if (!state.mappingsByWorkflow[workflowId]) {
        state.mappingsByWorkflow[workflowId] = {
          nodes: [],
          edges: [],
          connectionDetails: [],
        };
      }
      state.mappingsByWorkflow[workflowId].nodes = nodes;
    },
    setEdgesForWorkflow(state, action) {
      const { workflowId, edges } = action.payload;
      if (!state.mappingsByWorkflow) state.mappingsByWorkflow = {};
      if (!state.mappingsByWorkflow[workflowId]) {
        state.mappingsByWorkflow[workflowId] = {
          nodes: [],
          edges: [],
          connectionDetails: [],
        };
      }
      state.mappingsByWorkflow[workflowId].edges = edges;
    },
    setConnectionDetailsForWorkflow(state, action) {
      const { workflowId, connectionDetails } = action.payload;
      if (!state.mappingsByWorkflow) state.mappingsByWorkflow = {};
      if (!state.mappingsByWorkflow[workflowId]) {
        state.mappingsByWorkflow[workflowId] = {
          nodes: [],
          edges: [],
          connectionDetails: [],
        };
      }
      state.mappingsByWorkflow[workflowId].connectionDetails =
        connectionDetails;
    },
    resetMappingForWorkflow(state, action) {
      const workflowId = action.payload;
      if (!state.mappingsByWorkflow) state.mappingsByWorkflow = {};
      state.mappingsByWorkflow[workflowId] = {
        nodes: [],
        edges: [],
        connectionDetails: [],
      };

      delete state.fuzzyLogicRunByWorkflow[workflowId];
      delete state.fieldMappingRunByWorkflow[workflowId];
      delete state.mappedEntitiesByWorkflow[workflowId];
      delete state.unmappedEntitiesByWorkflow[workflowId];
      delete state.fuzzyAppliedEntitiesByWorkflow[workflowId];
      delete state.hasVisitedMappingByWorkflow[workflowId];
      delete state.autoFuzzyEnabledByWorkflow[workflowId];
      delete state.expandAllTriggerByWorkflow?.[workflowId];
      delete state.blinkByWorkflow?.[workflowId];
    },
    resetAllMappings: () => initialState,
    deleteMappingForWorkflow(state, action) {
      const workflowId = action.payload;
      if (state.mappingsByWorkflow?.[workflowId])
        delete state.mappingsByWorkflow[workflowId];
      if (state.fuzzyLogicRunByWorkflow?.[workflowId])
        delete state.fuzzyLogicRunByWorkflow[workflowId];
      if (state.fieldMappingRunByWorkflow?.[workflowId])
        delete state.fieldMappingRunByWorkflow[workflowId];
      if (state.mappedEntitiesByWorkflow?.[workflowId])
        delete state.mappedEntitiesByWorkflow[workflowId];
      if (state.unmappedEntitiesByWorkflow?.[workflowId])
        delete state.unmappedEntitiesByWorkflow[workflowId];
      if (state.fuzzyAppliedEntitiesByWorkflow?.[workflowId])
        delete state.fuzzyAppliedEntitiesByWorkflow[workflowId];
      if (state.hasVisitedMappingByWorkflow?.[workflowId])
        delete state.hasVisitedMappingByWorkflow[workflowId];
      if (state.autoFuzzyEnabledByWorkflow?.[workflowId])
        delete state.autoFuzzyEnabledByWorkflow[workflowId];
      if (state.expandAllTriggerByWorkflow?.[workflowId])
        delete state.expandAllTriggerByWorkflow[workflowId];
      if (state.finalConfigPayloadByWorkflow?.[workflowId])
        delete state.finalConfigPayloadByWorkflow[workflowId];
      if (state.blinkByWorkflow?.[workflowId])
        delete state.blinkByWorkflow[workflowId];
    },
    removeEdgeAndUnmapFields: (state, action) => {
      const { workflowId, edgeId } = action.payload;

      const mapping = state.mappingsByWorkflow?.[workflowId];
      if (!mapping) return;

      const edges = mapping.edges || [];
      const nodes = mapping.nodes || [];

      const edgeToRemove = edges.find((edge) => edge.id === edgeId);
      console.log('edgeToRemove:', edgeToRemove);
      if (!edgeToRemove) return;

      const { source, target, sourceHandle, targetHandle } = edgeToRemove;

      const updatedNodes = nodes.map((node) => {
        if (![source, target].includes(node.id)) return node;

        if (!Array.isArray(node?.data?.subFields)) return node;

        const newSubFields = node.data.subFields.map((group) => {
          const newChildren = group.children.map((child) => {
            if (child.id === sourceHandle || child.id === targetHandle) {
              return { ...child, isMapped: false };
            }
            return child;
          });
          return { ...group, children: newChildren };
        });

        return {
          ...node,
          data: {
            ...node.data,
            subFields: newSubFields,
          },
        };
      });

      state.mappingsByWorkflow[workflowId].nodes = updatedNodes;
      state.mappingsByWorkflow[workflowId].edges = edges.filter(
        (edge) => edge.id !== edgeId,
      );
    },
    setAutoFuzzyEnabled: (state, action) => {
      const { workflowId, enabled } = action.payload;
      if (!state.autoFuzzyEnabledByWorkflow)
        state.autoFuzzyEnabledByWorkflow = {};
      state.autoFuzzyEnabledByWorkflow[workflowId] = enabled;
    },
    setMappedEntities: (state, action) => {
      const { workflowId, mappedEntities } = action.payload;
      if (!state.mappedEntitiesByWorkflow) state.mappedEntitiesByWorkflow = {};
      // state.mappedEntitiesByWorkflow[workflowName] = mappedEntities;
      // state.mappedEntitiesByWorkflow[workflowName] = {
      //   Source: mappedEntities.Source || [],
      //   Destination: mappedEntities.Destination || [],
      // };

      const prev = state.mappedEntitiesByWorkflow[workflowId] || {
        Source: [],
        Destination: [],
      };
      state.mappedEntitiesByWorkflow[workflowId] = {
        Source: Array.from(
          new Set([...prev.Source, ...(mappedEntities.Source || [])]),
        ),
        Destination: Array.from(
          new Set([...prev.Destination, ...(mappedEntities.Destination || [])]),
        ),
      };
    },
    setUnmappedEntities: (state, action) => {
      const { workflowId, unmappedEntities } = action.payload;
      if (!state.unmappedEntitiesByWorkflow)
        state.unmappedEntitiesByWorkflow = {};
      // state.unmappedEntitiesByWorkflow[workflowName] = unmappedEntities;
      state.unmappedEntitiesByWorkflow[workflowId] = {
        Source: unmappedEntities.Source || [],
        Destination: unmappedEntities.Destination || [],
      };
    },
    setHasVisitedMapping: (state, action) => {
      const { workflowId } = action.payload;
      if (!state.hasVisitedMappingByWorkflow)
        state.hasVisitedMappingByWorkflow = {};
      state.hasVisitedMappingByWorkflow[workflowId] = true;
    },
    clearFuzzyStateForWorkflow(state, action) {
      const { workflowId } = action.payload;
      if (state.fuzzyLogicRunByWorkflow)
        delete state.fuzzyLogicRunByWorkflow[workflowId];
      if (state.fieldMappingRunByWorkflow)
        delete state.fieldMappingRunByWorkflow[workflowId];
      if (state.autoFuzzyEnabledByWorkflow)
        delete state.autoFuzzyEnabledByWorkflow[workflowId];
      if (state.fuzzyAppliedEntitiesByWorkflow)
        delete state.fuzzyAppliedEntitiesByWorkflow[workflowId];

      if (!state.mappedEntitiesByWorkflow) state.mappedEntitiesByWorkflow = {};
      if (!state.unmappedEntitiesByWorkflow)
        state.unmappedEntitiesByWorkflow = {};

      state.mappedEntitiesByWorkflow[workflowId] = {
        Source: [],
        Destination: [],
      };
      state.unmappedEntitiesByWorkflow[workflowId] = {
        Source: [],
        Destination: [],
      };
    },
    setExpandAllTrigger(state, action) {
      const { workflowId, trigger } = action.payload;
      if (!state.expandAllTriggerByWorkflow)
        state.expandAllTriggerByWorkflow = {};
      state.expandAllTriggerByWorkflow[workflowId] = trigger;
    },
    setNodeHeight: (state, action) => {
      const { workflowId, nodeId, height } = action.payload;
      if (!state.nodeHeightsByWorkflow) state.nodeHeightsByWorkflow = {};
      if (!state.nodeHeightsByWorkflow[workflowId])
        state.nodeHeightsByWorkflow[workflowId] = {};
      state.nodeHeightsByWorkflow[workflowId][nodeId] = height;
    },

    setFinalConfigPayloadForWorkflow(state, action) {
      const { workflowId, finalConfigPayload } = action.payload;
      if (!state.finalConfigPayloadByWorkflow)
        state.finalConfigPayloadByWorkflow = {};
      state.finalConfigPayloadByWorkflow[workflowId] = finalConfigPayload;
    },
    setBlinkByWorkflow(state, action) {
      const { workflowId, button, value } = action.payload;
      if (!state.blinkByWorkflow) {
        state.blinkByWorkflow = {};
      }

      if (!state.blinkByWorkflow[workflowId]) {
        state.blinkByWorkflow[workflowId] = { save: false, execute: false };
      }
      state.blinkByWorkflow[workflowId][button] = value;
    },
    removeFinalConfigById(state, action) {
      const { workflowId, id } = action.payload;
      const list = state.finalConfigPayloadByWorkflow?.[workflowId];
      if (!Array.isArray(list)) return;

      state.finalConfigPayloadByWorkflow[workflowId] = list.filter(
        (cfg) => cfg?.id !== id,
      );
    },
    setFilterExpressionForWorkflow(state, action) {
      const { workflowId, filterExpression } = action.payload;
      if (!state.filterExpressionByWorkflow) {
        state.filterExpressionByWorkflow = {};
      }
      state.filterExpressionByWorkflow[workflowId] = filterExpression || null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchResolvedFields.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchResolvedFields.fulfilled, (state, action) => {
        state.loading = false;
        const { workflowId, result } = action.payload;

        if (!state.resolvedFieldsByWorkflow)
          state.resolvedFieldsByWorkflow = {};
        state.resolvedFieldsByWorkflow[workflowId] = result;

        const newNodes = buildInitialNodes(result);

        console.log('new nodes::', newNodes);

        const previouslyMappedFields = new Set();
        const mismatchedFields = new Set();
        const previousColorByKey = new Set();
        const prevNodes = state.mappingsByWorkflow[workflowId]?.nodes || [];

        console.log('prevNodes::::', prevNodes);

        prevNodes.forEach((node) => {
          if (node.data?.subFields) {
            node.data.subFields.forEach((group) => {
              group.children.forEach((field) => {
                const key = `${node.id}::${field.id}`;
                if (field.isMapped) previouslyMappedFields.add(key);
                if (field.isMismatch) mismatchedFields.add(key);
                if (field.color) previousColorByKey.add(key);
              });
            });
          }
        });

        if (!state.mappingsByWorkflow) state.mappingsByWorkflow = {};
        if (!state.mappingsByWorkflow[workflowId]) {
          state.mappingsByWorkflow[workflowId] = {
            nodes: [],
            edges: [],
            connectionDetails: [],
          };
        }

        const oldNodes = state.mappingsByWorkflow[workflowId]?.nodes || [];
        const oldEdges = state.mappingsByWorkflow[workflowId]?.edges || [];
        const functionNodes = oldNodes.filter((n) => n.type === 'functionNode');

        let updatedNodes = [...newNodes, ...functionNodes];

        updatedNodes = updatedNodes.map((node) => {
          if (!node.data?.subFields) return node;
          const updatedSubFields = node.data.subFields.map((group) => ({
            ...group,
            children: group.children.map((field) => {
              const key = `${node.id}::${field.id}`;
              return {
                ...field,
                isMapped: previouslyMappedFields.has(key),
                isMismatch: mismatchedFields.has(key),
                color: previousColorByKey.has(key),
              };
            }),
          }));
          return {
            ...node,
            data: { ...node.data, subFields: updatedSubFields },
          };
        });

        const validSourceHandles = new Set();
        const validDestinationHandles = new Set();
        // Map for entity+field matching (case-insensitive)
        const sourceHandlesByEntityField = new Map();
        const destHandlesByEntityField = new Map();

        // Helper to extract entity and field from field ID
        const extractEntityField = (fieldId) => {
          if (!fieldId || typeof fieldId !== 'string') return null;
          const parts = fieldId.split('__');
          if (parts.length >= 3) {
            return {
              entity: parts[parts.length - 2]?.toLowerCase() || '',
              field: parts[parts.length - 1]?.toLowerCase() || '',
            };
          } else if (parts.length === 2) {
            return {
              entity: parts[0]?.toLowerCase() || '',
              field: parts[1]?.toLowerCase() || '',
            };
          }
          return null;
        };

        newNodes.forEach((n) => {
          if (n.type === 'sourceNode' && n.data?.subFields) {
            n.data.subFields.forEach((g) =>
              g.children.forEach((f) => {
                const handleKey = `${n.id}::${f.id}`;
                validSourceHandles.add(handleKey);
                const entityField = extractEntityField(f.id);
                if (entityField && entityField.entity && entityField.field) {
                  const entityFieldKey = `${entityField.entity}::${entityField.field}`;
                  sourceHandlesByEntityField.set(entityFieldKey, handleKey);
                }
              }),
            );
          }
          if (n.type === 'destinationNode' && n.data?.subFields) {
            n.data.subFields.forEach((g) =>
              g.children.forEach((f) => {
                const handleKey = `${n.id}::${f.id}`;
                validDestinationHandles.add(handleKey);
                const entityField = extractEntityField(f.id);
                if (entityField && entityField.entity && entityField.field) {
                  const entityFieldKey = `${entityField.entity}::${entityField.field}`;
                  destHandlesByEntityField.set(entityFieldKey, handleKey);
                }
              }),
            );
          }
        });

        console.log('validSourceHandles:::', validSourceHandles);

        let updatedEdges = oldEdges
          .map((e) => {
            const sourceNode = updatedNodes.find((n) => n.id === e.source);
            const targetNode = updatedNodes.find((n) => n.id === e.target);
            if (!sourceNode || !targetNode) return null;

            let sourceHandle = e.sourceHandle;
            let targetHandle = e.targetHandle;

            // Check source handle
            if (sourceNode.type === 'sourceNode' && e.sourceHandle) {
              const exactHandleKey = `${sourceNode.id}::${e.sourceHandle}`;
              if (!validSourceHandles.has(exactHandleKey)) {
                // Try entity+field matching
                const oldEntityField = extractEntityField(e.sourceHandle);
                if (
                  oldEntityField &&
                  oldEntityField.entity &&
                  oldEntityField.field
                ) {
                  const entityFieldKey = `${oldEntityField.entity}::${oldEntityField.field}`;
                  const newHandleKey =
                    sourceHandlesByEntityField.get(entityFieldKey);
                  if (newHandleKey) {
                    // Extract the actual handle from the key
                    sourceHandle = newHandleKey.split('::')[1];
                    // Update source node ID if it changed
                    const newSourceNodeId = newHandleKey.split('::')[0];
                    if (newSourceNodeId !== e.source) {
                      const newSourceNode = updatedNodes.find(
                        (n) => n.id === newSourceNodeId,
                      );
                      if (newSourceNode) {
                        return {
                          ...e,
                          source: newSourceNodeId,
                          sourceHandle,
                        };
                      }
                    }
                  } else {
                    return null; // No match found
                  }
                } else {
                  return null; // Can't extract entity+field
                }
              }
            }

            // Check destination handle
            if (targetNode.type === 'destinationNode' && e.targetHandle) {
              const exactHandleKey = `${targetNode.id}::${e.targetHandle}`;
              if (!validDestinationHandles.has(exactHandleKey)) {
                // Try entity+field matching
                const oldEntityField = extractEntityField(e.targetHandle);
                if (
                  oldEntityField &&
                  oldEntityField.entity &&
                  oldEntityField.field
                ) {
                  const entityFieldKey = `${oldEntityField.entity}::${oldEntityField.field}`;
                  const newHandleKey =
                    destHandlesByEntityField.get(entityFieldKey);
                  if (newHandleKey) {
                    // Extract the actual handle from the key
                    targetHandle = newHandleKey.split('::')[1];
                    // Update target node ID if it changed
                    const newTargetNodeId = newHandleKey.split('::')[0];
                    if (newTargetNodeId !== e.target) {
                      const newTargetNode = updatedNodes.find(
                        (n) => n.id === newTargetNodeId,
                      );
                      if (newTargetNode) {
                        return {
                          ...e,
                          source: sourceHandle ? e.source : e.source,
                          sourceHandle,
                          target: newTargetNodeId,
                          targetHandle,
                        };
                      }
                    }
                  } else {
                    return null; // No match found
                  }
                } else {
                  return null; // Can't extract entity+field
                }
              }
            }

            // Return edge with potentially updated handles
            if (
              sourceHandle !== e.sourceHandle ||
              targetHandle !== e.targetHandle
            ) {
              return {
                ...e,
                sourceHandle,
                targetHandle,
              };
            }
            return e;
          })
          .filter(Boolean);

        const nodeIdsInEdges = new Set();
        updatedEdges.forEach((e) => {
          nodeIdsInEdges.add(e.source);
          nodeIdsInEdges.add(e.target);
        });
        updatedNodes = updatedNodes.filter(
          (n) => n.type !== 'functionNode' || nodeIdsInEdges.has(n.id),
        );

        const validNodeIds = new Set(updatedNodes.map((n) => n.id));
        updatedEdges = updatedEdges.filter(
          (e) => validNodeIds.has(e.source) && validNodeIds.has(e.target),
        );

        const pruned = pruneInvalidEdgesAndFunctionChains({
          nodes: updatedNodes,
          edges: updatedEdges,
        });

        // const pruned = pruneAndSyncInvalidEdges({ nodes: updatedNodes, edges: updatedEdges });

        state.mappingsByWorkflow[workflowId] = {
          nodes: pruned.nodes,
          edges: pruned.edges,
          connectionDetails:
            state.mappingsByWorkflow[workflowId]?.connectionDetails || [],
        };
      })

      .addCase(fetchResolvedFields.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(runFuzzyLogicIfNeeded.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(runFuzzyLogicIfNeeded.fulfilled, (state, action) => {
        //eslint-disable-next-line
        const { result, workflowId, alreadyRun } = action.payload;
        state.loading = false;

        if (!alreadyRun) {
          if (!state.fuzzyLogicRunByWorkflow) {
            state.fuzzyLogicRunByWorkflow = {};
          }
          state.fuzzyLogicRunByWorkflow[workflowId] = true;
        }
      })
      .addCase(runFuzzyLogicIfNeeded.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(runFieldMappingIfNeeded.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(runFieldMappingIfNeeded.fulfilled, (state, action) => {
        const { result, workflowId, alreadyRun } = action.payload;
        state.loading = false;

        if (!alreadyRun) {
          if (!state.fieldMappingRunByWorkflow) {
            state.fieldMappingRunByWorkflow = {};
          }

          state.fieldMappingRunByWorkflow[workflowId] = true;
          state.mappedFields = result?.mapped_fields || {};
        }
      })
      .addCase(runFieldMappingIfNeeded.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getSavedMapping.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSavedMapping.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(getSavedMapping.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch mapping';
      })
      .addCase(saveMappingThunk.pending, (state) => {
        state.saving = true;
      })
      //eslint-disable-next-line
      .addCase(saveMappingThunk.fulfilled, (state, action) => {
        state.saving = false;
        state.saveSuccess = true;
      })
      .addCase(saveMappingThunk.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload || 'Error saving mapping';
      });
  },
});

export const {
  setNodesForWorkflow,
  setEdgesForWorkflow,
  setConnectionDetailsForWorkflow,
  resetMappingForWorkflow,
  deleteMappingForWorkflow,
  removeEdgeAndUnmapFields,
  resetAllMappings,
  setAutoFuzzyEnabled,
  setMappedEntities,
  setUnmappedEntities,
  setHasVisitedMapping,
  clearFuzzyStateForWorkflow,
  setExpandAllTrigger,
  setNodeHeight,
  setFinalConfigPayloadForWorkflow,
  setBlinkByWorkflow,
  removeFinalConfigById,
  setFilterExpressionForWorkflow,
} = mappingSlice.actions;

export default mappingSlice.reducer;
